import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  forwardRef,
  inject,
  input,
  type OnInit,
  signal,
} from '@angular/core';
import {
  ControlErrorComponent,
  type ControlValueAccessorOnChange,
  type ControlValueAccessorOnTouched,
  HasRequiredValidatorPipe,
} from '@pct-ng/form';
import {
  type AbstractFileUploadService,
  FILE_UPLOAD_SERVICE_TOKEN,
  type FileUploadResponse,
  IsRemovablePipe,
  isSuccessFileUploadResponse,
  ShowPreviewPipe,
  GetFileUploadResponseId,
  getFileUploadResponseId,
} from '@pct-ng/feature-file-upload-native';
import {
  type ControlValueAccessor,
  NG_VALIDATORS,
  type Validator,
  type AbstractControl,
  type ValidationErrors,
  FormArray,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput, MatError } from '@angular/material/input';
import { deepDistinctUntilChanged } from '@pct/rxjs';
import { assertIsNotNil } from '@pct-ts/assert';
import { MatCard, MatCardContent, MatCardImage } from '@angular/material/card';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { CustomMatSvgIconDirective } from '@pct-ng/icon';
import { FileToUrlPipe, ImagePreviewService } from '@pct-ng/feature-image-native';
import { MaterialFileUploadComponent } from '@pct-ng/feature-file-upload-material';
import { openConfirmDialog } from '@pct-ng/feature-message-material';
import { TranslocoPipe } from '@jsverse/transloco';
import { isNil } from '@pct-ts/typeguard';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IncludesPipe, IsNilPipe } from '@pct-ng/common/pipe';
import { deepEqual } from 'fast-equals';
import type { GalleryItem, GalleryItemForm } from './models';
import { FindItemGroupById } from './pipe/find-item-group-by-id';
import {
  LanguageCodes,
  formArrayMaxLength,
  type LanguageCode,
  FieldLabelComponent,
} from '@cappus/shared-common';
import { map } from 'rxjs';
import { ensureItemsArray } from '@pct-ts/common';
import type { MaybeArray } from '@pct-ts/typing';
import { GALLERY_IMAGE_CONFIG_INJECTION_TOKEN } from './config-injection-token/gallery-image-config-injection-token';

@Component({
  selector: 'cappus-gallery-control',
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    ControlErrorComponent,
    MatError,
    MatCard,
    MatCardContent,
    MatIconButton,
    MatIcon,
    CustomMatSvgIconDirective,
    MatCardImage,
    FileToUrlPipe,
    IsRemovablePipe,
    ShowPreviewPipe,
    TranslocoPipe,
    IsNilPipe,
    IncludesPipe,
    FindItemGroupById,
    MaterialFileUploadComponent,
    GetFileUploadResponseId,
    FieldLabelComponent,
    HasRequiredValidatorPipe,
  ],
  templateUrl: './gallery-control.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    ImagePreviewService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GalleryControlComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => GalleryControlComponent),
      multi: true,
    },
  ],
})
export class GalleryControlComponent implements OnInit, ControlValueAccessor, Validator {
  protected readonly getFileUploadResponseId = getFileUploadResponseId;
  readonly #destroyRef = inject(DestroyRef);
  readonly #imagePreviewService = inject(ImagePreviewService);
  readonly #fileUploadService: AbstractFileUploadService<string> | null = inject(
    FILE_UPLOAD_SERVICE_TOKEN,
    { optional: true },
  );
  readonly service = input<AbstractFileUploadService<string> | null>();
  readonly i18nScope = input<string>();
  readonly i18nPrefix = input.required<string>();
  readonly submitted = input<boolean>(false);
  readonly isSubtitleRequired = input.required<boolean>();

  protected readonly selectedFiles = signal<FileUploadResponse<string>[]>([]);
  protected readonly effectiveService = computed(() => {
    const service = this.service() ?? this.#fileUploadService;
    assertIsNotNil(service, 'Not defined file upload service');
    return service;
  });
  readonly galleryImageConfig = inject(GALLERY_IMAGE_CONFIG_INJECTION_TOKEN);

  readonly startedLanguages = input.required<LanguageCode[]>();

  #onValidatorChange?: () => void;

  protected readonly galleryIdsControl = new FormControl<MaybeArray<string>>([], {
    nonNullable: true,
  });

  protected readonly itemsFormArray = new FormArray<GalleryItemForm>([], {
    validators: [formArrayMaxLength(this.galleryImageConfig.maxFiles)],
  });

  // Ez azért kell hogy a dropzone hiba esetén piros színű legyen
  protected hasError = computed(
    () => this.itemsFormArray.touched && this.itemsFormArray.hasError('formArrayMaxLength'),
  );

  readonly nameAttribute = input.required<string>();
  protected readonly isDisabled = signal(false);

  #onChange!: ControlValueAccessorOnChange<GalleryItem[]>;
  #onTouched!: ControlValueAccessorOnTouched;

  constructor() {
    effect(() => {
      const startedLangs = this.startedLanguages();
      this.isSubtitleRequired();
      this.#updateSubtitleValidators(startedLangs);
    });

    effect(() => {
      if (this.submitted()) {
        this.itemsFormArray.markAllAsTouched();
      }
    });
  }

  ngOnInit(): void {
    // Szinkronizáció(bentről kifelé: kép törlés, hozzáadás): Ha a belső ImageGalleryComponent ID listája változik
    this.galleryIdsControl.valueChanges
      .pipe(
        deepDistinctUntilChanged(),
        map(ids => ensureItemsArray(ids)),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(ids => {
        const currentItems = this.itemsFormArray.getRawValue();
        const newItems: GalleryItem[] = ids.map(id => {
          const existingItem = currentItems.find(item => item.id === id);
          return existingItem ?? this.#createEmptyGalleryItem(id);
        });

        this.#onChange(newItems);
        this.#onTouched();
        this.#syncItemsFormArray(newItems);
      });

    // Szinkronizáció: bentről kifelé ha a feliratok változnak
    this.itemsFormArray.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      const items = this.itemsFormArray.getRawValue();
      this.#onChange(items);
      this.#onTouched();
      this.#onValidatorChange?.();
    });
  }

  writeValue(value: GalleryItem[] | null): void {
    if (isNil(value)) {
      this.galleryIdsControl.setValue([], { emitEvent: false });
      this.itemsFormArray.clear({ emitEvent: false });
      return;
    }

    const ids = value.map(item => item.id);
    if (!deepEqual(ids, this.galleryIdsControl.value)) {
      this.galleryIdsControl.setValue(ids, { emitEvent: false });
    }

    this.#syncItemsFormArray(value);
  }

  registerOnChange(fn: (value: GalleryItem[]) => void): void {
    this.#onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.#onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
    if (isDisabled) {
      this.galleryIdsControl.disable({ emitEvent: false });
      this.itemsFormArray.disable({ emitEvent: false });
    } else {
      this.galleryIdsControl.enable({ emitEvent: false });
      this.itemsFormArray.enable({ emitEvent: false });
    }
  }

  registerOnValidatorChange(fn: () => void): void {
    this.#onValidatorChange = fn;
  }

  #syncItemsFormArray(items: GalleryItem[]): void {
    // Frissítjük a FormArray-t, hogy megfeleljen az aktuális items-nek (sorrend és tartalom)
    const currentFormGroups = [...this.itemsFormArray.controls];
    this.itemsFormArray.clear({ emitEvent: false });

    items.forEach(item => {
      let formGroup = currentFormGroups.find(group => group.controls.id.value === item.id);

      if (isNil(formGroup)) {
        formGroup = this.#createItemFormGroup(item);
      } else {
        // Frissítjük az értéket, ha változott
        formGroup.patchValue(item, { emitEvent: false });
      }
      this.itemsFormArray.push(formGroup, { emitEvent: false });
    });
    this.#updateSubtitleValidators(this.startedLanguages());
    this.itemsFormArray.markAsTouched();
    this.#onValidatorChange?.();
  }

  #createItemFormGroup(item: GalleryItem): GalleryItemForm {
    const translationsArray = new FormArray(
      LanguageCodes.map(langCode => {
        const translation = item.translations.find(t => t.language === langCode) || {
          language: langCode,
          subtitle: '',
        };

        return new FormGroup({
          language: new FormControl<LanguageCode>(translation.language, {
            nonNullable: true,
          }),
          subtitle: new FormControl<string>(translation.subtitle, {
            nonNullable: true,
            validators: Validators.maxLength(255),
          }),
        });
      }),
    );

    return new FormGroup({
      id: new FormControl<string>(item.id, { nonNullable: true }),
      translations: translationsArray,
    });
  }

  #updateSubtitleValidators(startedLangs: LanguageCode[]): void {
    this.itemsFormArray.controls.forEach(itemFormGroup => {
      itemFormGroup.controls.translations.controls.forEach(translationGroup => {
        const language = translationGroup.controls.language.value;
        const subtitleControl = translationGroup.controls.subtitle;

        subtitleControl.removeValidators(Validators.required);
        if (this.isSubtitleRequired()) {
          if (startedLangs.includes(language)) {
            subtitleControl.addValidators(Validators.required);
          }
        }
        subtitleControl.updateValueAndValidity({ emitEvent: false });
      });
    });
  }

  #createEmptyGalleryItem(id: string): GalleryItem {
    return {
      id,
      translations: LanguageCodes.map(langCode => ({
        language: langCode,
        subtitle: '',
      })),
    };
  }

  protected onClickRemove(fileResponse: FileUploadResponse<string>): void {
    openConfirmDialog({
      title: 'COMMON.DELETE_CONFIRM_DIALOG.TITLE',
      message: 'COMMON.DELETE_CONFIRM_DIALOG.MESSAGE',
    }).subscribe(messageResult => {
      if (messageResult?.result === 'SUBMIT') {
        if (isSuccessFileUploadResponse(fileResponse)) {
          this.galleryIdsControl.patchValue(
            ensureItemsArray(this.galleryIdsControl.value).filter(id => id !== fileResponse.id),
          );
        }
      }
    });
  }

  protected onFilesSelect($event: FileUploadResponse<string>[]): void {
    this.selectedFiles.set($event);
  }

  protected onClickOpenPreview(file: File): void {
    this.#imagePreviewService.openImagePreview({ file });
  }

  validate(_: AbstractControl): ValidationErrors | null {
    if (this.itemsFormArray.invalid) {
      return this.itemsFormArray.errors ?? { galleryControlInvalid: true };
    }
    return null;
  }
}
